import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import ControlGroup from '@amzn/meridian/control-group';
import Checkbox from '@amzn/meridian/checkbox';
import RadioButton from '@amzn/meridian/radio-button';
import AssociateScanChart, { ChartSeries } from './AssociateScanChart';
import { getAssociateScanMetric, ScanMetricMap } from './data/associate-api';
import { getRefreshInterval, generateColor } from './util';
import { deriveScanState, AssociateOption } from './scan-metrics';

/**
 * AssociateScanChartView — associate + granularity controls beside the scan-rate chart
 * (React + Meridian, ported from v1). Owns the selection/granularity state (was AssociateScanContext),
 * fetches scan metrics on mount + poll, and computes the chart series for the presentational chart.
 */
const AssociateScanChartView: React.FC = () => {
  const [scanMetrics, setScanMetrics] = useState<ScanMetricMap>({});
  const [associateList, setAssociateList] = useState<AssociateOption[]>([]);
  const [selectedAssociates, setSelectedAssociates] = useState<string[]>([]);
  const [granularity, setGranularity] = useState<string>('15m');

  // Refs mirror the currently committed list + selection so an async poll can tell a first load
  // (auto-select every associate) from a refresh (preserve the user's still-valid selection),
  // matching v1 AssociateScanReducer's `isFirstLoad = state.associateList.length === 0`.
  const associateListRef = useRef<AssociateOption[]>([]);
  associateListRef.current = associateList;
  const selectedAssociatesRef = useRef<string[]>([]);
  selectedAssociatesRef.current = selectedAssociates;

  const getData = useCallback(async () => {
    const raw = await getAssociateScanMetric();
    // deriveScanState mirrors v1 RECEIVE_DATA: derive the cumulative bucket, build the sorted
    // associate list, and auto-select all associates on first load (preserve selection on polls).
    const next = deriveScanState(raw, associateListRef.current, selectedAssociatesRef.current);
    setScanMetrics(next.scanMetrics);
    setAssociateList(next.associateList);
    setSelectedAssociates(next.selectedAssociates);
  }, []);

  useEffect(() => {
    getData();
    const interval = setInterval(() => {
      if (!document.hidden) getData();
    }, getRefreshInterval());
    return () => clearInterval(interval);
  }, [getData]);

  const { labels, datasets } = useMemo(() => {
    const granularityData = scanMetrics[granularity];
    if (!granularityData || Object.keys(granularityData).length === 0) return { labels: [], datasets: [] as ChartSeries[] };

    const available = Object.keys(granularityData);
    const first = available[0] && granularityData[available[0]];
    const times: string[] = Array.isArray(first)
      ? first.map((item) => {
          const t = new Date(item.timeStampVal).toTimeString().split(' ')[0].split(':');
          return `${t[0]}:${t[1]}`;
        })
      : [];

    const series = selectedAssociates
      .map((email, index) => {
        const data = granularityData[email];
        if (!Array.isArray(data)) return null;
        return {
          label: email.replace('@amazon.com', ''),
          data: data.map((item) => item.metricValue || 0),
          borderColor: generateColor(index),
          fill: false,
        } as ChartSeries;
      })
      .filter(Boolean) as ChartSeries[];

    return { labels: times, datasets: series };
  }, [scanMetrics, selectedAssociates, granularity]);

  return (
    <Row widths={['grid-3', 'grid-9']} spacingInset="medium large">
      <div>
        <Column spacingInset="none none medium">
          <h3>Associates</h3>
          <div style={{ overflow: 'auto', maxHeight: associateList.length > 4 ? '125px' : 'auto' }}>
            {associateList.length > 0 && (
              <ControlGroup name="associates" onChange={setSelectedAssociates} value={selectedAssociates}>
                {associateList.map((associate) => (
                  <Checkbox key={associate.key} value={associate.key}>
                    {associate.name}
                  </Checkbox>
                ))}
              </ControlGroup>
            )}
          </div>
        </Column>

        <Column spacingInset="medium none none">
          <h3>Time Granularity</h3>
          <ControlGroup name="granularity" onChange={setGranularity} value={granularity}>
            <RadioButton value="15m">15 Minutes</RadioButton>
            <RadioButton value="30m">30 Minutes</RadioButton>
            <RadioButton value="1h">1 Hour</RadioButton>
            <RadioButton value="cumulative">Cumulative</RadioButton>
          </ControlGroup>
        </Column>
      </div>

      <div style={{ height: '354px', position: 'relative' }}>
        <AssociateScanChart labels={labels} datasets={datasets} />
      </div>
    </Row>
  );
};

export default AssociateScanChartView;
